import React, { useEffect, useState, useRef } from 'react';
import {
  useTable,
  usePagination,
  useRowSelect,
  useSortBy,
} from 'react-table';
import ReactPaginate from 'react-paginate';
import '../assets/css/Table.css';
import prev from '../assets/icons/Vector 2.svg';
import SortFilterDropdown from './SortByFilter';

const SortIcon = ({ isSorted, isSortedDesc }) => (
  <svg width="12" height="15" viewBox="0 0 7 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 3L3.5 1L6 3" stroke={isSorted && !isSortedDesc ? '#6B778C' : '#A0A0A0'} strokeWidth="1.1" strokeLinecap="round" />
    <path d="M1 7L3.5 9L6 7" stroke={isSortedDesc ? '#6B778C' : '#A0A0A0'} strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const AdvanceTable = ({
  data,
  columns,
  selectedData,
  handlePageChange,
  searchValue,
  pageCounts,
  currentPage,
  onSelectionChange,
  checkbox = false,
  handleRowClick,
  expandable = false,   // 🔹 new prop
  ExpandComponent,      // 🔹 custom component to render inside expanded row
  rowClassName,
  handleSort, sortField,      // ✅ receive here
  sortOrder,      // ✅ receive here
  showPagination = true,
}) => {

  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [activeSortColumn, setActiveSortColumn] = useState(null); // 🔹 track which column menu is open

  const prevSelectedIdsRef = useRef([]);
  const rowRefs = useRef({});

  useEffect(() => {
    if (selectedRow) return;

    const flattened = selectedData?.flat ? selectedData.flat() : selectedData;
    if (flattened && flattened.length > 0) {
      const firstItem = flattened[0];
      setSelectedRow(firstItem.id || firstItem.user_id || firstItem.unique_id);
    } else {
      setSelectedRow(data[0]?.id || null);
    }
  }, [selectedData, data, selectedRow]);

  const toggleRow = (rowId) => {
    setSelectedRow(rowId);
    if (expandable) {
      setExpandedRowId((prev) => (prev === rowId ? null : rowId));
    }
  };

  const tableHooks = checkbox
    ? [
      useSortBy,
      usePagination,
      useRowSelect,
      (hooks) => {
        hooks.visibleColumns.push((columns) => [
          {
            id: 'selection',
            Header: ({ rows }) => {
              const selectableRows = rows.filter(
                (row) => !row.original.is_verified
              );

              const allSelected = selectableRows.every((row) => row.isSelected);
              const someSelected = selectableRows.some((row) => row.isSelected);

              return (
                <div>
                  {selectableRows.length > 0 && (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={() => {
                        const value = !allSelected;
                        selectableRows.forEach((row) =>
                          row.toggleRowSelected(value)
                        );
                      }}
                    />
                  )}
                </div>
              );
            },
            Cell: ({ row }) => {
              const shouldHideCheckbox = row.original.is_verified;

              return (
                <div>
                  {!shouldHideCheckbox && (
                    <input
                      type="checkbox"
                      {...row.getToggleRowSelectedProps({
                        onChange: (e) => {
                          const checked = e.target.checked;
                          const currentUniqueId = row.original.unique_id;

                          rows.forEach((r) => {
                            if (r.original.unique_id === currentUniqueId) {
                              r.toggleRowSelected(checked);
                            }
                          });
                        },
                      })}
                    />
                  )}
                </div>
              );
            },
          },
          ...columns,
        ]);
      },
    ]
    : [useSortBy, usePagination];

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    gotoPage,
    rows,
    state: { pageIndex },
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: showPagination ? 10 : data.length,
      },
    },
    ...tableHooks
  );

  useEffect(() => {
    if (!checkbox) return;

    const selectedRowsData = selectedFlatRows.map((row) => row.original);

    const prevSelectedIds = prevSelectedIdsRef.current.map(
      (row) => row.unique_id || row.user_id
    );
    const currentSelectedIds = selectedRowsData.map(
      (row) => row.unique_id || row.user_id
    );

    const hasChanged =
      currentSelectedIds.length !== prevSelectedIds.length ||
      !currentSelectedIds.every((id, index) => id === prevSelectedIds[index]);

    if (hasChanged) {
      prevSelectedIdsRef.current = selectedRowsData;
      if (onSelectionChange) {
        onSelectionChange(selectedRowsData);
      }
    }
  }, [selectedFlatRows, onSelectionChange, checkbox]);

  const fallbackPageCount = Math.ceil(data.length / 10);

  return (
    <div className="table-container">
      <div className="table-body-wrapper">
        <table {...getTableProps()} className={`student-table ${rowClassName ? rowClassName : ''}`} >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    style={{
                      cursor: column.canSort ? 'pointer' : 'default',
                      backgroundColor: '#f9f9f9',
                      color: '#6b778c',
                    }}
                    className={`${column.id === "noc_status" ? "th-noc-status" : ""}`}
                  >
                    {column.headerJustify ? (
                      /* ✅ Only wrap when headerJustify is present */
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: column.headerJustify,
                          gap: '6px',
                        }}
                      >
                        {column.render('Header')}

                        {column.canSort && (
                          <span
                            style={{
                              marginLeft: '10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              position: 'relative',
                            }}
                          >
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveSortColumn(
                                  activeSortColumn === column.id ? null : column.id
                                );
                              }}
                            >
                              <SortIcon
                                isSorted={column.isSorted}
                                isSortedDesc={column.isSortedDesc}
                              />
                            </span>

                            {activeSortColumn === column.id && (
                              <SortFilterDropdown
                                columnId={column.id}
                                onSort={handleSort}
                                currentSortOrder={sortField === column.id ? sortOrder : null}
                                onClose={() => setActiveSortColumn(null)}
                              />
                            )}
                          </span>
                        )}
                      </div>
                    ) : (
                      /* ✅ Default header (no flex wrapper) */
                      <>
                        {column.render('Header')}
                        {column.canSort && (
                          <span
                            style={{
                              marginLeft: '10px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              position: 'relative',
                            }}
                          >
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveSortColumn(
                                  activeSortColumn === column.id ? null : column.id
                                );
                              }}
                            >
                              <SortIcon
                                isSorted={column.isSorted}
                                isSortedDesc={column.isSortedDesc}
                              />
                            </span>

                            {activeSortColumn === column.id && (
                              <SortFilterDropdown
                                columnId={column.id}
                                onSort={handleSort}
                                currentSortOrder={sortField === column.id ? sortOrder : null}
                                onClose={() => setActiveSortColumn(null)}
                              />
                            )}
                          </span>
                        )}
                      </>
                    )}

                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              const rowId = row.original.id || row.original.user_id;
              const isExpanded = expandedRowId === rowId;

              return (
                <React.Fragment key={rowId}>
                  <tr
                    {...row.getRowProps()}
                    ref={(el) => {
                      if (el) rowRefs.current[rowId] = el;
                    }}

                    onClick={() => {
                      toggleRow(rowId);
                      handleRowClick?.(rowId, row);
                    }}
                    // className={`${selectedRow === rowId ? 'active-row' : ''} ${row.isSelected ? 'checkbox-selected-row' : ''
                    //   }`}
                    className={`
                              ${selectedRow === rowId ? "active-row" : ""}
                              ${row.isSelected ? "checkbox-selected-row" : ""}
                              ${row.cells.some(c => c.column.id === "all_precheck_names") ? "row-prechecks" : ""}
                              ${row.cells.some(c => c.column.id === "course_name") ? "row-course" : ""}
                            `}

                  >
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}
                        className={`${cell.column.className || ""} ${cell.column.id === "noc_status" ? "td-noc-status" : ""}`}>{cell.render('Cell')}</td>
                    ))}
                    {/* {row.cells.map((cell) => {
                      // Conditionally style td based on column id
                      const tdStyle = cell.column.id === "all_precheck_names"
                        ? { whiteSpace: "normal", height: "auto", verticalAlign: "top" }
                        : {};
                        console.log("tdstyle")
                      return (
                        <td {...cell.getCellProps()} style={tdStyle}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })} */}

                  </tr>

                  {expandable && isExpanded && (
                    <tr>
                      <td colSpan={columns.length}>
                        {ExpandComponent ? (
                          <ExpandComponent row={row.original} />
                        ) : (
                          <div style={{ padding: '10px' }}>
                            No ExpandComponent provided
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPagination && (<div className="pagination-container">
        <ReactPaginate
          previousLabel={<img src={prev} alt="Previous" />}
          nextLabel={
            <svg
              width="7"
              height="12"
              viewBox="0 0 7 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.5 1L5.5 6L0.5 11"
                stroke="#7F56DA"
                strokeLinecap="round"
              />
            </svg>
          }
          breakLabel="..."
          pageCount={pageCounts || fallbackPageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={
            pageCounts
              ? handlePageChange
              : (selectedPage) => gotoPage(selectedPage.selected)
          }
          forcePage={pageCounts ? currentPage : pageIndex}
          containerClassName="pagination"
          activeClassName="active"
          disabledClassName="disabled"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item"
          nextClassName="page-item"
        />
      </div>)}
    </div>
  );
};

export default AdvanceTable;
